import { useState, useEffect, useMemo } from "react";
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase"; 
import { onAuthStateChanged, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { isSuperAdminEmail } from "../utils/superAdminEmails";
import ProjectsExplorer from "../components/portal/ProjectsExplorer";
import { useAuth } from "../context/AuthProvider";

function Home() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isCEO, setIsCEO] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true); // Track if we're still checking user access
  const navigate = useNavigate();
  const { loading: authLoading, user: authUser } = useAuth();


  // 🔥 Firebase auth check
  useEffect(() => {
    if (authLoading) return undefined;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setCheckingAccess(true);
        
        if (currentUser.email) {
          try {
            const normalizedEmail = currentUser.email.toLowerCase().trim();
            
            // Super admins (CEO emails) see all projects.
            const isUserCEO = isSuperAdminEmail(normalizedEmail);
            setIsCEO(isUserCEO);
            
            // Ensure user exists in /workspaces/ai-apps-dashboard/users/{uid} without scanning whole collection.
            const userRef = doc(db, "workspaces", "ai-apps-dashboard", "users", currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) {
              await setDoc(userRef, {
                email: normalizedEmail,
                name: currentUser.displayName || normalizedEmail.split('@')[0],
                fullName: currentUser.displayName || normalizedEmail.split('@')[0],
                photo: currentUser.photoURL || null,
                role: isUserCEO ? "super_admin" : "user",
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isActive: true
              }, { merge: true });
            }
          } catch (error) {
            console.error("Error checking user access:", error);
          } finally {
            setCheckingAccess(false);
          }
        }
      } else {
        navigate("/auth/login"); // agar login nahi hai → sidha login pe bhej do
      }
    });
    return () => unsubscribe();
  }, [navigate, authLoading]);

  // 🔥 Firestore se projects fetch
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const [projectsSnap, leProjectsSnap] = await Promise.all([
          getDocs(collection(db, "workspaces/ai-apps-dashboard/projects")),
          getDocs(collection(db, "workspaces/ai-apps-dashboard/le-small-projects")),
        ]);

        const mainProjects = projectsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          projectType: "main",
        }));
        const leProjects = leProjectsSnap.docs.map((doc) => ({
          id: `le-${doc.id}`,
          originalId: doc.id,
          ...doc.data(),
          projectType: "le",
          // Live visibility logic uses assignedUsers; LE form stores selectedUsers.
          assignedUsers: Array.isArray(doc.data().selectedUsers) ? doc.data().selectedUsers : [],
        }));
        const data = [...mainProjects, ...leProjects];
        
        // Sort by sequence if available, otherwise by createdAt
        data.sort((a, b) => {
          if (typeof a.sequence === 'number' && typeof b.sequence === 'number') {
            return a.sequence - b.sequence;
          }
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateA - dateB;
        });
        
        setProjects(data);
      } catch (err) {
        console.error("❌ Error fetching projects:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    localStorage.removeItem("user");
    navigate("/auth/login");
  };

  const normalizedUserEmail = user?.email ? user.email.toLowerCase().trim() : "";
  const normalizedUserDepartment =
    typeof authUser?.department === "string" ? authUser.department.toLowerCase().trim() : "";

  const visibleProjects = useMemo(() => {
    return projects.filter((p) => {
      const isLeProject = p.projectType === "le";

      // LE projects: strict visibility by selected users only.
      if (isLeProject) {
        if (checkingAccess) return false;
        if (!normalizedUserEmail) return false;

        const selectedUsersList = Array.isArray(p.selectedUsers) ? p.selectedUsers : [];
        const isAssignedToLeProject = selectedUsersList.some((idOrEmail) => {
          const normalized = (idOrEmail || "").toString().toLowerCase().trim();
          return normalized === normalizedUserEmail;
        });

        return isAssignedToLeProject;
      }

      // Home page shows only projects that have a live link field filled (including "NA")
      // Projects with "NA" will show but won't have the "Open App" icon
      const link = p.link?.trim?.();
      const hasLiveLinkField = link && link.length > 0;
      if (!hasLiveLinkField) return false;

      // CEOs see all projects
      if (isCEO) return true;

      // Hide project if current user was marked inactive for this project in Access Management
      if (user?.email) {
        const inactiveEmails = (p.inactiveAssignedUsers || [])
          .map((e) => (e || "").toLowerCase().trim())
          .filter(Boolean);
        if (inactiveEmails.includes(user.email.toLowerCase().trim())) {
          return false;
        }
      }

      // Only apply visibility rules after access check is done
      if (checkingAccess) return false;

      // Normal user: show only assigned projects
      const assignedUsersList = Array.isArray(p.assignedUsers)
        ? p.assignedUsers
        : Array.isArray(p.selectedUsers)
        ? p.selectedUsers
        : [];
      const isAssignedToThisProject =
        normalizedUserEmail &&
        assignedUsersList.some((idOrEmail) => {
          const normalized = (idOrEmail || "").toString().toLowerCase().trim();
          return normalized === normalizedUserEmail;
        });

      if (isAssignedToThisProject) return true;

      // Department-based access: if the user's department is selected on the project, show it
      // (covers employees who joined the department after access was last saved),
      // unless an admin explicitly deselected this user.
      if (!normalizedUserEmail || !normalizedUserDepartment) return false;
      const projectDepartments = (Array.isArray(p.department) ? p.department : [p.department])
        .map((d) => (typeof d === "string" ? d.toLowerCase().trim() : ""))
        .filter(Boolean);
      if (!projectDepartments.includes(normalizedUserDepartment)) return false;
      const excludedEmails = (Array.isArray(p.excludedUsers) ? p.excludedUsers : [])
        .map((e) => (e || "").toString().toLowerCase().trim());
      return !excludedEmails.includes(normalizedUserEmail);
    });
  }, [projects, isCEO, user?.email, checkingAccess, normalizedUserEmail, normalizedUserDepartment]);

  return (
    <div className="h-full overflow-hidden">
      {loading || checkingAccess ? (
        <div className="h-full flex items-center justify-center">
          <p className="text-sm text-slate-500">Loading projects...</p>
        </div>
      ) : (
        <ProjectsExplorer
          projects={visibleProjects}
          mode="live"
          defaultView="detail"
        />
      )}
    </div>
  );
}

export default Home;
